import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommerceService } from '@common/services/commerce.service';
import { ImproveFinaAccessService } from '@common/services/improve-fina-access.service';
import { SmsPackage, SmsService } from '@common/services/sms.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface SmsPackageOption {
    id: string;
    title: string;
    description?: string;
    quantity: number;
    priceUsd: number;
    bestValue?: boolean;
}

@Component({
    selector: 'fina-sms-messages',
    templateUrl: './sms-messages.component.html',
    styleUrls: ['./sms-messages.component.scss'],
})
export class SmsMessagesComponent implements OnInit, OnDestroy {
    /** `card`: franja de saldo en próximo pago. `modal`: selector de paquetes. */
    @Input() layout: 'card' | 'modal' = 'card';
    @Input() selectedPackageId: string = '';
    @Output() buyClick = new EventEmitter<void>();
    @Output() selectedPackageChange = new EventEmitter<SmsPackageOption>();

    packages: SmsPackageOption[] = [];
    loadingPackages = true;
    packagesError = false;
    readonly skeletonSlots = [1, 2, 3];
    private readonly ivaRate = 0.16;
    private readonly destroy$ = new Subject<void>();

    constructor(public commerceService: CommerceService, private smsService: SmsService, private improveFinaAccess: ImproveFinaAccessService) {}

    /**
     * «Comprar más SMS» (layout card, pestaña Planes) es una compra de «Mejora tu Fina»: solo el dueño del
     * comercio (2026-11-03; el api responde 403 a `/sms/buy*` para soporte y asociados).
     */
    get canBuy(): boolean {
        return this.improveFinaAccess.canOperateSync();
    }

    ngOnInit(): void {
        if (this.layout === 'modal') {
            this.loadPackages();
        } else {
            this.loadingPackages = false;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get availableSms(): number {
        const commerce = this.commerceService._commerce$?.value;
        if (!commerce) {
            return 0;
        }
        return Number(commerce.limitSms || 0) + Number(commerce.purchasedSms || 0);
    }

    get selectedPackage(): SmsPackageOption | undefined {
        return this.packages.find(p => p.id === this.selectedPackageId) ?? this.packages[0];
    }

    get subtotalUsd(): number {
        return this.selectedPackage?.priceUsd ?? 0;
    }

    get ivaUsd(): number {
        return Number((this.subtotalUsd * this.ivaRate).toFixed(2));
    }

    get totalUsd(): number {
        return Number((this.subtotalUsd + this.ivaUsd).toFixed(2));
    }

    unitPriceLabel(pkg: SmsPackageOption): string {
        if (!pkg.quantity) {
            return '';
        }
        const unit = pkg.priceUsd / pkg.quantity;
        return `≈ $${unit.toFixed(2)} por SMS`;
    }

    selectPackage(pkg: SmsPackageOption): void {
        this.selectedPackageId = pkg.id;
        this.selectedPackageChange.emit(pkg);
    }

    onBuyClick(): void {
        this.buyClick.emit();
    }

    private loadPackages(): void {
        // Si ya hay paquetes en memoria (p. ej. tras cache del servicio), no mostrar skeleton.
        if (this.packages.length > 0) {
            this.loadingPackages = false;
        } else {
            this.loadingPackages = true;
        }
        this.packagesError = false;

        this.smsService
            .getSmsPackages$()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: items => {
                    this.packages = this.mapPackages(items);
                    this.ensureSelection();
                    this.loadingPackages = false;
                },
                error: () => {
                    this.packages = [];
                    this.packagesError = true;
                    this.loadingPackages = false;
                },
            });
    }

    private mapPackages(items: SmsPackage[]): SmsPackageOption[] {
        const mapped = (items || []).map(item => ({
            id: item._id,
            title: item.title,
            description: item.description,
            quantity: Number(item.amountmsm) || 0,
            priceUsd: Number(item.amount) || 0,
            bestValue: false,
        }));

        if (mapped.length === 0) {
            return mapped;
        }

        let bestId = mapped[0].id;
        let bestUnit = Number.POSITIVE_INFINITY;
        for (const pkg of mapped) {
            if (!pkg.quantity) {
                continue;
            }
            const unit = pkg.priceUsd / pkg.quantity;
            if (unit < bestUnit) {
                bestUnit = unit;
                bestId = pkg.id;
            }
        }

        return mapped.map(pkg => ({ ...pkg, bestValue: pkg.id === bestId && mapped.length > 1 }));
    }

    private ensureSelection(): void {
        if (!this.packages.length) {
            this.selectedPackageId = '';
            return;
        }

        const stillValid = this.packages.some(p => p.id === this.selectedPackageId);
        if (stillValid) {
            const current = this.packages.find(p => p.id === this.selectedPackageId);
            if (current) {
                this.selectedPackageChange.emit(current);
            }
            return;
        }

        const preferred = this.packages.find(p => p.bestValue) ?? this.packages[0];
        this.selectPackage(preferred);
    }
}
